import { useEffect, useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Check, X, Gift } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { supabase } from "@/integrations/supabase/client";
import { Link } from "react-router-dom";

type Plan = {
  id: string;
  name: string;
  price: number;
  description: string;
  is_popular: boolean;
  display_order: number;
  features: string[];
};

type Addon = {
  id: string;
  name: string;
  price: number;
  description: string | null;
};

export default function Pricing() {
  const [plans, setPlans] = useState<Plan[]>([]);
  const [addons, setAddons] = useState<Addon[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchPricingData();
  }, []);

  const fetchPricingData = async () => {
    try {
      // Fetch plans
      const { data: plansData, error: plansError } = await supabase
        .from("pricing_plans")
        .select("*")
        .order("display_order", { ascending: true });

      if (plansError) throw plansError;

      // Fetch features for each plan
      const plansWithFeatures = await Promise.all(
        (plansData || []).map(async (plan) => {
          const { data: featuresData } = await supabase
            .from("plan_features")
            .select("*")
            .eq("plan_id", plan.id)
            .order("feature_order", { ascending: true });

          return {
            ...plan,
            features: featuresData?.map((f) => f.feature_name) || [],
          };
        })
      );

      setPlans(plansWithFeatures);

      // Fetch addons
      const { data: addonsData, error: addonsError } = await supabase
        .from("pricing_addons")
        .select("*")
        .order("name", { ascending: true });

      if (addonsError) throw addonsError;
      setAddons(addonsData || []);
    } catch (error) {
      console.error("Error fetching pricing data:", error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  const basicPlan = plans.find((p) => p.name === "Basic");
  const standardPlan = plans.find((p) => p.name === "Standard");
  const premiumPlan = plans.find((p) => p.name === "Premium");
  
  // Determine grid columns based on number of plans
  const gridCols = plans.length === 2 ? "md:grid-cols-2" : "md:grid-cols-3";

  return (
    <div className="space-y-8 animate-fade-in">
      <div className="text-center">
        <h1 className="text-4xl font-bold text-foreground mb-4">Choose Your Plan</h1>
        <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
          Flexible pricing to suit every PTA. Start with the basics or unlock everything with Standard.
        </p>
      </div>

      {/* Free Sponsored Option Banner */}
      <Card className="shadow-card max-w-5xl mx-auto bg-gradient-primary border-2 border-primary">
        <CardContent className="py-6">
          <div className="flex items-center justify-center gap-4 text-center">
            <Gift className="h-8 w-8 text-primary-foreground" />
            <div>
              <h3 className="text-xl font-bold text-primary-foreground mb-1">
                FREE Sponsored Membership Available!
              </h3>
              <p className="text-primary-foreground/90">
                Local businesses can sponsor your PTA membership at no cost to you.{" "}
                <Link to="/sponsorship" className="underline font-semibold hover:text-white transition-colors">
                  Learn more about sponsored memberships
                </Link>
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      <div className={`grid ${gridCols} gap-6 max-w-6xl mx-auto`}>
        {plans.map((plan) => (
          <Card 
            key={plan.id}
            className={`shadow-card hover:shadow-elevated transition-shadow relative ${
              plan.is_popular ? 'border-2 border-primary shadow-elevated' : ''
            }`}
          >
            {plan.is_popular && (
              <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                <Badge className="bg-primary text-primary-foreground px-4 py-1">Most Popular</Badge>
              </div>
            )}
            <CardHeader>
              <CardTitle className="text-2xl">{plan.name} Plan</CardTitle>
              <CardDescription>{plan.description}</CardDescription>
              <div className="pt-4">
                <span className="text-4xl font-bold text-primary">£{plan.price.toFixed(2)}</span>
                <span className="text-muted-foreground">/year</span>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <Button className="w-full" size="lg">
                Get {plan.name}
              </Button>

              <div className="space-y-3 pt-4">
                <p className="font-semibold text-sm">
                  {plan.name === "Basic" ? "What's included:" : `Everything in ${plans.find(p => p.display_order < plan.display_order)?.name || "previous"}, plus:`}
                </p>
                {plan.features.map((feature, i) => (
                  <div key={i} className="flex items-start gap-3">
                    <Check className="h-5 w-5 text-green-600 shrink-0 mt-0.5" />
                    <span className="text-foreground">{feature}</span>
                  </div>
                ))}
              </div>

              {plan.name === "Basic" && addons.length > 0 && (
                <div className="pt-4 border-t border-border">
                  <p className="text-sm font-semibold mb-2">Add-ons Available:</p>
                  <div className="space-y-2">
                    {addons.map((addon) => (
                      <div key={addon.id} className="flex justify-between items-center text-sm">
                        <span className="text-muted-foreground">{addon.name}</span>
                        <span className="font-semibold text-primary">£{addon.price.toFixed(2)}/yr</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Referral Program */}
      <Card className="shadow-card max-w-5xl mx-auto">
        <CardHeader>
          <CardTitle>Referral Program</CardTitle>
          <CardDescription>Earn discounts by referring other PTAs</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid md:grid-cols-2 gap-6">
            <div className="space-y-4">
              <div className="p-4 bg-gradient-primary rounded-lg text-primary-foreground">
                <h3 className="font-semibold text-lg mb-2">How It Works</h3>
                <ul className="space-y-2 text-sm">
                  <li>• Refer another PTA to PTA Hub</li>
                  <li>• Earn 5-10% discount on your renewal</li>
                  <li>• Stack discounts up to 50% maximum</li>
                  <li>• Receive discount codes via email</li>
                </ul>
              </div>
            </div>

            <div className="space-y-4">
              <div className="p-4 border border-border rounded-lg">
                <h4 className="font-semibold mb-3">Your Referral Status</h4>
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-sm text-muted-foreground">PTAs Referred:</span>
                    <span className="font-semibold">3</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-muted-foreground">Current Discount:</span>
                    <span className="font-semibold text-green-600">20%</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-muted-foreground">Savings on Renewal:</span>
                    <span className="font-semibold text-primary">£26.00</span>
                  </div>
                </div>
                <div className="mt-4 pt-4 border-t border-border">
                  <p className="text-sm text-muted-foreground mb-2">Your referral code:</p>
                  <div className="flex gap-2">
                    <code className="flex-1 p-2 bg-muted rounded font-mono text-sm">HILLSIDE-PTA-REF</code>
                    <Button variant="outline" size="sm">
                      Copy
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Feature Comparison */}
      <Card className="shadow-card max-w-6xl mx-auto">
        <CardHeader>
          <CardTitle>Feature Comparison</CardTitle>
          <CardDescription>Detailed breakdown of plan features</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-border">
                  <th className="text-left py-3 px-4">Feature</th>
                  {plans.map((plan) => (
                    <th key={plan.id} className="text-center py-3 px-4">{plan.name}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {Array.from(new Set(plans.flatMap(p => p.features))).map((feature, i) => (
                  <tr key={i} className="border-b border-border">
                    <td className="py-3 px-4">{feature}</td>
                    {plans.map((plan) => (
                      <td key={plan.id} className="text-center">
                        {plan.features.includes(feature) ? (
                          <Check className="h-5 w-5 text-green-600 mx-auto" />
                        ) : (
                          <X className="h-5 w-5 text-muted-foreground mx-auto" />
                        )}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
